import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LessonAuthoringService from "../../apis/LessonAuthoringService";

const stripHtml = (html = "") => html.replace(/<[^>]*>/g, "").trim();

const SearchResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    results: initialResults = [],
    nextToken: initialNextToken = null,
    query,
    courseId,
  } = location.state || {};

  const [results, setResults] = useState(initialResults);
  const [nextToken, setNextToken] = useState(initialNextToken);
  const [loadingMore, setLoadingMore] = useState(false);
  const [noMoreResults, setNoMoreResults] = useState(false);

  // ✅ Update page when searching again (same route)
  useEffect(() => {
    if (!location.state) return;

    setResults(location.state.results || []);
    setNextToken(location.state.nextToken || null);
    setNoMoreResults(false);
  }, [location.state]);

  const handleBack = () => {
    // ✅ Go back if possible, else fallback
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/"); // or your main page route
    }
  };

  const loadMore = async () => {
    if (loadingMore || noMoreResults) return;

    setLoadingMore(true);

    try {
      const resp = await LessonAuthoringService.searchCourseContent(
        courseId,
        query,
        null,
        nextToken
      );

      const newItems = resp.items || [];

      if (newItems.length === 0) {
        setNoMoreResults(true);
        setNextToken(null);
        return;
      }

      setResults((prev) => {
        const existingIds = new Set(prev.map((i) => i.lessonId));
        const filtered = newItems.filter(
          (item) => !existingIds.has(item.lessonId)
        );
        return [...prev, ...filtered];
      });

      setNextToken(resp.nextToken || null);
      if (!resp.nextToken) setNoMoreResults(true);
    } catch (err) {
      console.error("Load more failed", err);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <Box sx={{ pt: "60px", px: 2 }}>
      {/* 🔹 Header with Back button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 2,
          position: "sticky",
          top: 0,
          backgroundColor: "#fff",
          zIndex: 10,
          pb: 1,
        }}
      >
        <IconButton onClick={handleBack} size="small">
          <ArrowBackIcon />
        </IconButton>

        <Typography variant="h6">Search Results</Typography>
      </Box>

      {results.length > 0 ? (
        <>
          <List>
            {results.map((result, index) => (
              <ListItem
                key={`${result.lessonId}-${index}`}
                button
                onClick={() =>
                  navigate(
                    `/Modules/Course/${result.courseId}/Module/${result.moduleId}/Chapter/${result.chapterId}/Lesson/${result.lessonId}`,
                    {
                      state: {
                        lessonName: result.name,
                        contentId: result.lessonId,
                        fromSearch: true,
                      },
                    }
                  )
                }
              >
                <ListItemText
                  primary={
                    <Typography sx={{ color: "#1976d2", fontWeight: 600 }}>
                      {result.name}
                    </Typography>
                  }
                  secondary={
                    <>
                      {result.title && (
                        <Typography variant="body2">
                          {stripHtml(result.title)}
                        </Typography>
                      )}
                      <Typography variant="caption">
                        Module {result.moduleId} • Chapter {result.chapterId}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>

          {/* 🔹 Load More */}
          {!noMoreResults && nextToken && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Button
                variant="outlined"
                onClick={loadMore}
                disabled={loadingMore}
                sx={{
                  px: 4,
                  borderRadius: "20px",
                  textTransform: "none",
                }}
              >
                {loadingMore ? "Loading..." : "Load more"}
              </Button>
            </Box>
          )}

          {/* 🔹 No More Results */}
          {noMoreResults && (
            <Typography align="center" sx={{ mt: 3, color: "text.secondary" }}>
              No more results
            </Typography>
          )}
        </>
      ) : (
        <Typography>No results found.</Typography>
      )}
    </Box>
  );
};

export default SearchResultsPage;
